import PropTypes from 'prop-types';
import { useMemo } from 'react';
import { useTheme } from '@mui/material/styles';
import { v4 as uuidv4 } from 'uuid';
import { useNavigate } from 'react-router-dom';
import {
	List,
	ListItem,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	Typography
} from '@mui/material';

// Icons
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import AnalyticsOutlinedIcon from '@mui/icons-material/AnalyticsOutlined';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import KeyIcon from '@mui/icons-material/Key';
import SpaIcon from '@mui/icons-material/Spa';
import PercentIcon from '@mui/icons-material/Percent';
import LocalPizzaIcon from '@mui/icons-material/LocalPizza';

// Our Components
import NavItemMenu from 'components/NavMenu/NavBarComponents/NavItemMenu';

// Routes
import {
	DASHBOARD_ROUTE,
	METRICS_ROUTE,
	RATES_ROUTE,
	ADVISORS_ROUTE,
	DB_TABLES_ROUTE,
	CLIENTS_ROUTE,
	QUERY_ENGINE_ROUTE,
	PYLON_LOAN_APPLICATION_ROUTE,
	PYLON_RATE_ROUTE,
	PYLON_BORROWER_DASHBOARD_ROUTE,
	KEY_ACTIONS_TODO_ROUTE,
	KEY_ACTIONS_CONFIGURE_ROUTE
} from 'routes';

const BASE_URL = process.env.REACT_APP_URL;

const ADMIN_NAV_CONFIG = [
	{
		route: DASHBOARD_ROUTE,
		Icon: DashboardOutlinedIcon,
		navText: 'Dashboard',
		isExternal: false,
		isMenu: false
	},
	{
		route: ADVISORS_ROUTE,
		Icon: PersonOutlineOutlinedIcon,
		navText: 'Advisors',
		isExternal: false,
		isMenu: false
	},
	{
		route: CLIENTS_ROUTE,
		Icon: PersonOutlineOutlinedIcon,
		navText: 'Clients',
		isExternal: false,
		isMenu: false
	},
	{
		route: DB_TABLES_ROUTE,
		Icon: AnalyticsOutlinedIcon,
		navText: 'DB Tables',
		isExternal: true,
		isMenu: false
	},
	{
		route: METRICS_ROUTE,
		Icon: ShowChartIcon,
		navText: 'Metrics',
		isExternal: true
	},
	{
		route: QUERY_ENGINE_ROUTE,
		Icon: QueryStatsIcon,
		navText: 'Query Engine',
		isExternal: false,
		isMenu: false
	},
	{
		route: RATES_ROUTE,
		Icon: AttachMoneyIcon,
		navText: 'Scrapped Rates',
		isExternal: true,
		isMenu: false
	},
	// {
	// 	route: KEY_ACTIONS_TODO_ROUTE,
	// 	Icon: KeyIcon,
	// 	navText: 'Key Actions',
	// 	isExternal: false,
	// 	isMenu: true,
	// 	menuItems: [
	// 		{
	// 			route: KEY_ACTIONS_TODO_ROUTE,
	// 			navText: 'Todo'
	// 		},
	// 		{
	// 			route: KEY_ACTIONS_CONFIGURE_ROUTE,
	// 			navText: 'Configure'
	// 		}
	// 	]
	// },
	{
		route: PYLON_RATE_ROUTE,
		Icon: PercentIcon,
		navText: 'Rate Lookup Tool',
		isExternal: false,
		isMenu: false
	},
	{
		route: PYLON_LOAN_APPLICATION_ROUTE,
		Icon: SpaIcon,
		navText: 'Pylon Loan Application',
		isExternal: false,
		isMenu: false
	},
	{
		route: PYLON_BORROWER_DASHBOARD_ROUTE,
		Icon: LocalPizzaIcon,
		navText: 'Pylon Borrower Dashboard',
		isExternal: false,
		isMenu: false
	}
];

/**
 *
 * @param {boolean} isMobile
 * @param {Object} colors
 * @returns
 */

const getNavConfig = (isMobile, colors, colorOverride) => {
	const { indigo, white } = colors;

	const hasColorOverride = colorOverride in colors;

	if (hasColorOverride) {
		// If there is a color override, use it
		const finalColor = colors[colorOverride];

		return isMobile
			? { color: finalColor, fontWeight: 100, variant: 'h2Gascogne' }
			: { color: finalColor, fontWeight: 600, variant: 'body2' };
	}

	return isMobile
		? {
				color: colorOverride ?? indigo,
				fontWeight: 100,
				variant: 'h2Gascogne'
		  }
		: {
				color: colorOverride ?? white,
				fontWeight: 600,
				variant: 'body2'
		  };
};

function DynamicNavMenu({ navMenuOptions, isMobile, colorOverride }) {
	const SoraTheme = useTheme();
	const navigate = useNavigate();

	const { primary } = SoraTheme.palette;

	const navConfig = useMemo(
		() => getNavConfig(isMobile, primary, colorOverride),
		[isMobile]
	);

	const { color, fontWeight, variant } = navConfig;

	return (
		<List>
			{navMenuOptions.map((navOption) => {
				const {
					route,
					Icon,
					navText,
					isExternal,
					isMenu,
					onClickCallback
				} = navOption;

				return (
					<ListItem
						key={uuidv4()}
						disablePadding
						onClick={() => {
							// if isMenu leave do not route
							if (isMenu) return;

							// most nav options are not menus
							const isReactRoute = !isExternal;
							if (isReactRoute) {
								navigate(route);
							} else {
								if (onClickCallback) {
									onClickCallback();
									return;
								} else {
									window.location.href = `${BASE_URL}/${route}`;
								}
							}
						}}
					>
						{!isMenu && (
							<ListItemButton>
								{!isMobile && (
									<ListItemIcon sx={{ paddingLeft: 2 }}>
										<Icon
											sx={{
												color,
												width: '24px'
											}}
										/>
									</ListItemIcon>
								)}
								<ListItemText>
									<Typography
										variant={variant}
										sx={{
											color,
											fontWeight
										}}
									>
										{navText}
									</Typography>
								</ListItemText>
							</ListItemButton>
						)}
						{isMenu && (
							<NavItemMenu
								navText={navText}
								sx={{
									color,
									fontWeight
								}}
								variant={variant}
								menuItems={navOption.menuItems}
								Icon={Icon}
							/>
						)}
					</ListItem>
				);
			})}
		</List>
	);
}

DynamicNavMenu.propTypes = {
	navMenuOptions: PropTypes.arrayOf(
		PropTypes.shape({
			route: PropTypes.string,
			Icon: PropTypes.object,
			navText: PropTypes.string
		})
	),
	isMobile: PropTypes.bool
};

DynamicNavMenu.defaultProps = {
	navMenuOptions: ADMIN_NAV_CONFIG,
	isMobile: false
};

export default DynamicNavMenu;
